import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, Subscription, finalize } from 'rxjs';
import { StatusLabelEnum } from 'src/app/components/status-label/status-label.component';
import { ExecutionCalculatedStatus, Playbook, PlaybookStatus } from 'src/app/models/api/playbooks/playbook.model';
import { FilterTypesEnum } from 'src/app/models/filters/filterTypes.enum';
import { PlaybooksFiltersModel } from 'src/app/models/filters/implementations/pageFilters/playbooksFilters.model';
import { PageWithFiltersEnum } from 'src/app/models/filters/pageWithFilters.enum';
import { ISelectedFilters } from 'src/app/models/filters/selectedFilters.interface';
import { MenuAction } from 'src/app/models/menuAction.model';
import { IPlaybookRowViewModel } from 'src/app/models/viewModels/playbookRowViewModel';
import { PlaybooksActionsService } from 'src/app/services/actions/playbooks/playbooks-actions/playbooks-actions.service';
import { PlaybooksSupportedActions } from 'src/app/services/actions/playbooks/playbooks-actions/playbooksSupportedActions';

import { HttpErrorResponse } from '@angular/common/http';
import { PlatformIconPaths } from 'src/app/models/constants/platform.constants';
import { FiltersService } from 'src/app/services/filters/filters.service';
import { HttpErrorHandlerService } from 'src/app/services/httpServices/httpErrorHandlerService/http-error-handler.service';
import { PlaybooksHttpServiceService } from 'src/app/services/httpServices/playbooksHttpService/playbooks-http-service.service';
import { LoggerService } from 'src/app/services/infra/logger/logger.service';
import { NotificationsService } from 'src/app/services/infra/notifications/notifications.service';
import { PermissionsService } from 'src/app/services/infra/permissionsService/permissions.service';

enum SortableColumns {
  Name = 'name',
  Status = 'status',
  LastExecutionAt = 'lastExecutionAt',
  CreatedAt = 'createdAt',
}

export interface PlaybookSortColumn {
  key: SortableColumns;
  isDesc: boolean;
}

@Component({
  selector: 'app-playbooks-list',
  templateUrl: './playbooks-list.component.html',
  styleUrls: ['./playbooks-list.component.scss'],
})
export class PlaybooksListComponent implements OnInit, OnDestroy {
  public isLoading$ = new BehaviorSubject<boolean>(true);
  public showNoData = false;
  public playbooksRowViewModel$ = new BehaviorSubject<IPlaybookRowViewModel[]>([]);
  public totalPlaybooks = -1;
  public filtersAreEmpty = true;
  public filterEnums = FilterTypesEnum;
  public optionsAreSelected = false;
  public pageFilters: PlaybooksFiltersModel;

  public sortableColumns = SortableColumns;
  public selectedSort: PlaybookSortColumn = {
    key: SortableColumns.CreatedAt,
    isDesc: true,
  };

  private _playbooksById: { [playbookId: string]: Playbook } = {};

  private _filtersSubscription: Subscription;

  constructor(
    public matDialog: MatDialog,
    public logger: LoggerService,
    public permissionsService: PermissionsService,
    private readonly _router: Router,
    private readonly _playbooksHttpService: PlaybooksHttpServiceService,
    private readonly _filtersService: FiltersService,
    private readonly _httpErrorHandlerService: HttpErrorHandlerService,
    private readonly _playbookActionsService: PlaybooksActionsService,
    private readonly _notificationService: NotificationsService
  ) {}

  public ngOnInit(): void {
    this.pageFilters = <PlaybooksFiltersModel>this._filtersService.getFilters(PageWithFiltersEnum.Playbooks);
    this.pageFilters.restoreSortColumnToDefaultIfNeeded();

    this._filtersSubscription = this.pageFilters.onUpdate$.subscribe((selectedFilters) => {
      this.filtersAreEmpty = Object.values(selectedFilters).every(
        (value) => value === undefined || (Array.isArray(value) && value.length === 0)
      );
      this.optionsAreSelected = this.pageFilters.getAllSelectedOptions().length > 0;
      this._fetchPlaybooks(selectedFilters);
    });
  }

  public ngOnDestroy(): void {
    this._filtersSubscription?.unsubscribe();
  }

  public onColumnSortClick(column: SortableColumns) {
    if (this.selectedSort.key == column) {
      this.selectedSort.isDesc = !this.selectedSort.isDesc;
    } else {
      this.selectedSort = {
        key: column,
        isDesc: true,
      };
    }

    this._fetchPlaybooks(this.pageFilters.getSelectedFilters());
  }

  public actionOnClick(action: MenuAction, playbookRow: IPlaybookRowViewModel): void {
    const playbook = this._playbooksById[playbookRow.rowId];

    switch (action.name) {
      case PlaybooksSupportedActions.Edit:
      case PlaybooksSupportedActions.View:
        this._navigateToPlaybook(playbookRow);
        return;

      case PlaybooksSupportedActions.Duplicate:
        this._router.navigateByUrl(`/app/playbooks/${playbookRow.rowId}/copy`);
        return;
    }

    const response = this._playbookActionsService.handleActionClicked(
      action.name as PlaybooksSupportedActions,
      playbook
    );

    switch (action.name) {
      case PlaybooksSupportedActions.Enable:
      case PlaybooksSupportedActions.Disable:
        this._handlePlaybookStatusChange(response);
        break;
      case PlaybooksSupportedActions.Delete:
        this._handlePlaybookDeletion(response);
        break;
    }
  }

  public onPlaybookRowClick = (playbookRow: IPlaybookRowViewModel) => this._navigateToPlaybook(playbookRow);

  private readonly _navigateToPlaybook = (playbookRow: IPlaybookRowViewModel) =>
    this._router.navigateByUrl(`/app/playbooks/${playbookRow.rowId}`);

  private _fetchPlaybooks(filterOptions: ISelectedFilters) {
    this.playbooksRowViewModel$.next([]);
    this.totalPlaybooks = 0;

    this.isLoading$.next(true);
    this._playbooksHttpService
      .getPlaybooks(filterOptions, [this.selectedSort])
      .pipe(finalize(() => this.isLoading$.next(false)))
      .subscribe({
        next: (response) => this._handlePlaybooksCollectionResponse(response),
        error: (httpError: HttpErrorResponse) => this._fetchRequestOnError(httpError),
      });
  }

  private _fetchRequestOnError(httpError: HttpErrorResponse) {
    this.showNoData = true;
    this._httpErrorHandlerService.handle(httpError);
  }

  private _handlePlaybooksCollectionResponse(playbooks: Playbook[]) {
    this.totalPlaybooks = playbooks.length;
    this.showNoData = playbooks.length == 0;

    if (playbooks.length > 0) {
      this._playbooksById = {};
      const playbooksViewModelTemp = playbooks.map((playbook) => {
        this._playbooksById[playbook.id] = playbook;
        return this._transformPlaybookResponse(playbook);
      });

      this.playbooksRowViewModel$.next(playbooksViewModelTemp);
    }
  }

  private _transformPlaybookResponse(playbook: Playbook): IPlaybookRowViewModel {
    return <IPlaybookRowViewModel>{
      rowId: playbook.id,
      name: playbook.name,
      createdAt: playbook.userActionTimestamps.createdAt,
      createdBy: playbook.userActionTimestamps.createdBy,
      platform: playbook.platform,
      platformIconPath: PlatformIconPaths[playbook.platform],
      integration: [playbook.integration.name],
      policies: playbook.policies.length > 0 ? playbook.policies.map((policy) => policy.name) : [],
      lastExecutionAt: playbook.lastExecutionAt,
      statusLabelEnum: playbook.status == PlaybookStatus.active ? StatusLabelEnum.active : StatusLabelEnum.disabled,
      executionCalculatedStatus:
        playbook.executionCalculatedStatus == ExecutionCalculatedStatus.warning ? StatusLabelEnum.warning : undefined,
      menuActions: this._playbookActionsService.getMenuActionForPlaybook(playbook),
      clickable: true,
    };
  }

  private _handlePlaybookStatusChange(actionResponse: Observable<string | undefined>) {
    this._refresh(actionResponse, 'Playbook status was updated, refreshing page', 'Playbook status updated failed');
  }

  private _handlePlaybookDeletion(actionResponse: Observable<string | undefined>) {
    this._refresh(actionResponse, 'Playbook was deleted', 'Playbook deletion failed');
  }

  private _refresh(actionResponse: Observable<string | undefined>, successMessage: string, errorMessage: string) {
    actionResponse.subscribe({
      next: (response: string | undefined) => {
        if (!response) return;

        this._notificationService.success(successMessage);
        this._fetchPlaybooks(this.pageFilters.getSelectedFilters());
      },
      error: (error) => this._notificationService.error(error.error.detail || errorMessage),
    });
  }
}
